import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Card,
  Space,
  Select,
  InputNumber,
  Button,
  Row,
  Col,
  Divider,
  Alert,
  Checkbox,
  DatePicker,
  Spin,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  UndoOutlined,
  CalculatorOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { useTariffs } from "../../hooks/useTariffs";
import { useTransportTypes } from "../../hooks/useTransportTypes";
import { useIncoterms } from "../../hooks/useIncoterms";
import { formatEuro } from "../BookingWizard/PricingCalculatorHelpers";
import { trackFeatureUsage } from "../../lib/posthog";
import { supabase } from "../../lib/supabase";
import dayjs from "dayjs";
import {
  calculatePricingFromTemplates,
  type PricingContext,
  type BookingData,
  type PricingWarning,
} from "../../services/unifiedPricingService";
import {
  getLmdCountries,
  getCartonsPerPallet,
  getPalletThreshold,
  CBM_PER_PALLET,
} from "../../services/lmdRatesService";
import {
  getSelectedTariffForRoute,
  hasActiveOrFutureTariff,
  isTariffValidOnDate,
} from "../../utils/calculatorTariffUtils";
import { useCostOverviewDisplayOrder } from "../../hooks/useCostOverviewDisplayOrder";
import { usePortToAddressDistance } from "../../hooks/usePortToAddressDistance";
import { getContainervervoerPrice } from "../../utils/containervervoerPricing";
import { DeliveryAddressAutocomplete } from "./DeliveryAddressAutocomplete";
import { ContainervervoerCalculationInfo } from "./ContainervervoerCalculationInfo";
import { WarehouseToAddressMap } from "./WarehouseToAddressMap";
import { useWarehouseToAddressRoute } from "../../hooks/useWarehouseToAddressRoute";
import { useLatestRequestId } from "../../hooks/useLatestRequestId";

const PRICING_CALC_DEBOUNCE_MS = 250;
import { PRELOADED_MAP_PATHS } from "../../config/portToWarehouseRoutes";

const { Title, Text } = Typography;
const { Option } = Select;

// Styled calculator components
const CalculatorContainer = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
`;

const DisplayLabel = styled(Text)`
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  display: block;
  margin-bottom: 0;
`;

const DisplayValue = styled.div`
  font-size: 26px;
  font-weight: bold;
  font-variant-numeric: tabular-nums;
  color: rgba(0, 0, 0, 0.85);
`;

const InputGroup = styled.div`
  margin-bottom: 6px;
`;

const Label = styled(Text)`
  display: block;
  margin-bottom: 2px;
  font-weight: 500;
  font-size: 12px;
`;

const CompactCard = styled(Card)`
  .ant-card-body {
    padding: 12px;
  }
`;

const CompactInputGroup = styled.div`
  margin-bottom: 8px;

  .ant-input-number,
  .ant-select {
    width: 100%;
  }
`;

const CompactLabel = styled(Text)`
  display: block;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 500;
`;

const TotalDisplay = styled.div`
  text-align: center;
  padding: 12px;
  margin-bottom: 12px;
`;

const TotalLabel = styled(Text)`
  display: block;
  color: rgba(0, 0, 0, 0.45);
  font-size: 11px;
  margin-bottom: 4px;
`;

const TotalValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: rgba(0, 0, 0, 0.85);
`;

const BookingTypeTile = styled(Button)<{ selected?: boolean }>`
  width: 100%;
  height: 36px;
  border: 2px solid ${(props) => (props.selected ? "#1890ff" : "#d9d9d9")};
  background: ${(props) => (props.selected ? "#e6f7ff" : "#fff")};
  color: ${(props) => (props.selected ? "#1890ff" : "rgba(0, 0, 0, 0.85)")};
  font-weight: ${(props) => (props.selected ? 600 : 400)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;

  &:hover {
    border-color: #1890ff;
    color: #1890ff;
  }
`;

const COUNTRY_CODE_MAP: Record<string, string> = {
  NETHERLANDS: "NL",
  BELGIUM: "BE",
  GERMANY: "DE",
  FRANCE: "FR",
  UNITED_KINGDOM: "GB",
  SPAIN: "ES",
  ITALY: "IT",
  POLAND: "PL",
  CZECH_REPUBLIC: "CZ",
  AUSTRIA: "AT",
  SWITZERLAND: "CH",
  DENMARK: "DK",
  SWEDEN: "SE",
  NORWAY: "NO",
  FINLAND: "FI",
  PORTUGAL: "PT",
  GREECE: "GR",
  HUNGARY: "HU",
  ROMANIA: "RO",
  BULGARIA: "BG",
  CROATIA: "HR",
  SLOVAKIA: "SK",
  SLOVENIA: "SI",
  LITHUANIA: "LT",
  LATVIA: "LV",
  ESTONIA: "EE",
  IRELAND: "IE",
  LUXEMBOURG: "LU",
  MALTA: "MT",
  CYPRUS: "CY",
};

export type CalculatorMode = "full" | "compact" | "popup";

export interface CalculatorCoreProps {
  mode?: CalculatorMode;
  initialValues?: {
    transportTypeId?: number | null;
    bookingType?: "LCL" | "FCL" | null;
    incotermId?: number | null;
    route?: string | null;
    weight?: number;
    volume?: number;
    cartons?: number;
    totalValue?: number;
    lmdCountry?: string;
  };
  onClose?: () => void;
  showHeader?: boolean;
  showFullButton?: boolean;
  onNavigateToFull?: () => void;
}

export const CalculatorCore: React.FC<CalculatorCoreProps> = ({
  mode = "full",
  initialValues = {},
  onClose,
  showHeader = true,
  showFullButton = false,
  onNavigateToFull,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tariffs, loading: loadingTariffs } = useTariffs();
  const { transportTypes, loading: loadingTransportTypes } = useTransportTypes();
  const { incoterms, loading: loadingIncoterms } = useIncoterms();
  const sortCostOverview = useCostOverviewDisplayOrder();

  const isCompact = mode === "compact";
  const isPopup = mode === "popup";

  // Calculator inputs - initialize from props or defaults
  const [transportTypeId, setTransportTypeId] = useState<number | null>(
    initialValues.transportTypeId ?? null,
  );
  const [bookingType, setBookingType] = useState<"LCL" | "FCL" | null>(
    initialValues.bookingType ?? null,
  );
  const [incotermId, setIncotermId] = useState<number | null>(
    initialValues.incotermId ?? null,
  );
  const [selectedRoute, setSelectedRoute] = useState<string | null>(
    initialValues.route ?? null,
  );
  const [expectedDepartureDate, setExpectedDepartureDate] = useState(
    () => dayjs().add(7, "day"),
  );
  const [weight, setWeight] = useState<number>(initialValues.weight ?? 1);
  const [volume, setVolume] = useState<number>(() => {
    const n = Number(initialValues.volume);
    return Number.isFinite(n) && n > 0 ? n : 1;
  });
  const [cartons, setCartons] = useState<number>(initialValues.cartons ?? 1);
  const [hsCodes, setHsCodes] = useState<number>(1);
  const [totalValue, setTotalValue] = useState<number>(
    initialValues.totalValue ?? 0,
  );
  const [cartonsPerPallet, setCartonsPerPallet] = useState<number>(8);
  const [palletThreshold, setPalletThreshold] = useState<number>(8);
  const [containerSize, setContainerSize] = useState<number | null>(null);
  const [pickupFee, setPickupFee] = useState<number>(0);
  const [deliveryType, setDeliveryType] = useState<"delivery" | "storage">(
    "storage",
  );
  const [overridePallets, setOverridePallets] = useState<number | null>(null);
  const [forcePalletShipment, setForcePalletShipment] =
    useState<boolean>(false);
  /** Geruild = true → no fee; false → €15/pallet */
  const [europalletsExchanged, setEuropalletsExchanged] = useState(true);
  const [lmdCountry, setLmdCountry] = useState<string>(
    initialValues.lmdCountry || "NL",
  );
  const [availableLmdCountries, setAvailableLmdCountries] = useState<string[]>(
    [],
  );
  const [totalCost, setTotalCost] = useState<number>(0);
  const [hasError, setHasError] = useState<string | null>(null);
  const [pricingTemplatesResult, setPricingTemplatesResult] = useState<{
    totalCost: number;
    components: Array<{
      name: string;
      totalPrice: number;
      unitPrice?: number;
      quantity?: number;
      calculationType?: string;
      calculationDetails?: string;
      componentType?: string;
      componentId?: number;
    }>;
    matchedTemplates: Record<string, unknown>[];
    matchingCriteria: string[];
    warnings: PricingWarning[];
  } | null>(null);

  const [containerSizes, setContainerSizes] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [auxDataLoading, setAuxDataLoading] = useState(true);
  const [availableAdditionalCosts, setAvailableAdditionalCosts] = useState<
    any[]
  >([]);
  const [selectedAdditionalCosts, setSelectedAdditionalCosts] = useState<
    Set<number>
  >(new Set());
  const [additionalCostValues, setAdditionalCostValues] = useState<
    Record<number, number>
  >({});
  const [deliveryAddress, setDeliveryAddress] = useState<{
    lat: number;
    lon: number;
    formatted: string;
  } | null>(null);
  const { next: nextPricingRequest, isCurrent: isCurrentPricingRequest } =
    useLatestRequestId();

  const selectedAdditionalCostsKey = Array.from(selectedAdditionalCosts)
    .sort((a, b) => a - b)
    .join(",");
  const additionalCostValuesKey = JSON.stringify(additionalCostValues);

  const handleAdditionalCostValueChange = (
    costId: number,
    value: number | null,
  ) => {
    setAdditionalCostValues((prev) => {
      const next = { ...prev };
      if (value === null || Number.isNaN(value)) {
        delete next[costId];
      } else {
        next[costId] = value;
      }
      return next;
    });
  };

  const parseNumericValue = (value: unknown): number | null => {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const normalizeCountryCode = (
    country: string | null | undefined,
  ): string | null => {
    if (!country) return null;
    const upper = country.toUpperCase();
    return COUNTRY_CODE_MAP[upper] || upper;
  };

  // Reset route when transport type or booking type changes
  useEffect(() => {
    if (!initialValues.route) {
      setSelectedRoute(null);
    }
  }, [transportTypeId, bookingType, initialValues.route]);

  // Load container sizes, LMD countries, and pallet threshold
  useEffect(() => {
    const loadData = async () => {
      try {
        setAuxDataLoading(true);
        try {
          const countries = await getLmdCountries();
          setAvailableLmdCountries(countries?.length ? countries : ["NL", "BE"]);
        } catch (error) {
          console.error("Error fetching LMD countries:", error);
          setAvailableLmdCountries(["NL", "BE"]);
        }
        const threshold = await getPalletThreshold();
        setPalletThreshold(threshold);
        const cartonsPerPalletValue = await getCartonsPerPallet();
        setCartonsPerPallet(cartonsPerPalletValue);

        // Load container sizes (table name: container_size, singular)
        const { data: sizes } = await supabase
          .from("container_size")
          .select("id, name")
          .order("name");
        if (sizes) setContainerSizes(sizes);

        // Load additional costs
        const { data: costs } = await supabase
          .from("additional_costs")
          .select("*")
          .eq("is_active", true);
        if (costs) setAvailableAdditionalCosts(costs);
      } finally {
        setAuxDataLoading(false);
      }
    };
    loadData();
  }, []);

  // Generate route options - same logic as full calculator
  const routeOptions = useMemo(() => {
    if (!transportTypeId) return [];

    let availableTariffs = tariffs.filter(
      (tariff) => tariff.transport_type?.id === transportTypeId,
    );

    if (transportTypeId === 2 && bookingType) {
      const expectedBookingsTypeId = bookingType === "FCL" ? 2 : 1;
      availableTariffs = availableTariffs.filter(
        (tariff) => tariff.bookings_type?.id === expectedBookingsTypeId,
      );
    }

    const date = expectedDepartureDate.startOf("day");
    const findClosestForRoute = (routeTariffs: typeof availableTariffs) => {
      const validOnDate = routeTariffs.filter((t) =>
        isTariffValidOnDate(t, expectedDepartureDate),
      );
      if (validOnDate.length > 0) return validOnDate;
      const future = routeTariffs
        .filter((t) => t.from && dayjs(t.from).isAfter(date))
        .sort((a, b) => dayjs(a.from!).valueOf() - dayjs(b.from!).valueOf());
      if (future.length > 0) return future;
      const past = routeTariffs
        .filter((t) => t.to && dayjs(t.to).isBefore(date))
        .sort((a, b) => dayjs(b.to!).valueOf() - dayjs(a.to!).valueOf());
      if (past.length > 0) return past;
      return routeTariffs;
    };

    const routeGroups: Record<string, typeof availableTariffs> = {};
    const today = dayjs().startOf("day");

    availableTariffs.forEach((tariff) => {
      if (!tariff.origin_port?.id || !tariff.destination_port?.id) return;
      const routeKey = `${tariff.origin_port.id}-${tariff.destination_port.id}`;
      if (!routeGroups[routeKey]) {
        routeGroups[routeKey] = [];
      }
      routeGroups[routeKey].push(tariff);
    });

    return Object.keys(routeGroups)
      .map((routeKey) => {
        const tariffsInRoute = routeGroups[routeKey];
        // Only show routes with at least one active or future tariff (exclude fully expired routes)
        if (!hasActiveOrFutureTariff(tariffsInRoute, today)) return null;
        const validTariffs = findClosestForRoute(tariffsInRoute).sort(
          (a, b) => {
            if (!a.from) return 1;
            if (!b.from) return -1;
            return dayjs(b.from).valueOf() - dayjs(a.from).valueOf();
          },
        );
        if (validTariffs.length === 0) return null;

        const firstTariff = tariffsInRoute[0];
        const isFCL =
          transportTypeId === 2 && firstTariff.bookings_type?.id === 2;
        const currentTariff =
          isFCL && containerSize != null
            ? (validTariffs.find(
                (t) =>
                  t.container_size != null &&
                  Number(t.container_size.id) === Number(containerSize),
              ) ?? validTariffs[0])
            : validTariffs[0];

        const origin = firstTariff.origin_port?.name || "Unknown";
        const originCountry = firstTariff.origin_port?.country || "";
        const destination = firstTariff.destination_port?.name || "Unknown";
        const destinationCountry = firstTariff.destination_port?.country || "";

        let label = `${origin}${originCountry ? ` (${originCountry})` : ""} → ${destination}${destinationCountry ? ` (${destinationCountry})` : ""}`;

        if (transportTypeId === 1 && currentTariff.price) {
          label += ` (€${currentTariff.price.toFixed(2)}/kg)`;
        } else if (transportTypeId === 2 && currentTariff.price) {
          if (currentTariff.bookings_type?.id === 2) {
            const bySize = validTariffs.filter((t) => t.container_size != null);
            if (bySize.length > 1 && containerSize == null) {
              const prices = bySize.map((t) => t.price);
              const minP = Math.min(...prices);
              const maxP = Math.max(...prices);
              label +=
                minP < maxP
                  ? ` (€${minP.toFixed(2)}–${maxP.toFixed(2)}/container)`
                  : ` (€${minP.toFixed(2)}/container)`;
            } else {
              label += ` (€${currentTariff.price.toFixed(2)}/container)`;
            }
          } else {
            label += ` (€${currentTariff.price.toFixed(2)}/cbm)`;
          }
        }

        return {
          value: routeKey,
          label,
          key: routeKey,
        };
      })
      .filter(Boolean) as Array<{ value: string; label: string; key: string }>;
  }, [
    tariffs,
    transportTypeId,
    bookingType,
    expectedDepartureDate,
    containerSize,
  ]);

  // Selected tariff and fallback flag from shared logic (dashboard + full calculator parity)
  const { selectedTariff, isTariffFallback } = useMemo(
    () =>
      getSelectedTariffForRoute({
        tariffs,
        selectedRoute,
        transportTypeId,
        bookingType,
        expectedDepartureDate,
        volume,
        containerSize: containerSize != null ? Number(containerSize) : undefined,
      }),
    [
      tariffs,
      transportTypeId,
      bookingType,
      selectedRoute,
      volume,
      expectedDepartureDate,
      containerSize,
    ],
  );

  // Get origin and destination port IDs
  const originPortId = useMemo(() => {
    if (selectedTariff?.origin_port?.id) return selectedTariff.origin_port.id;
    if (!selectedRoute) return null;
    const parts = selectedRoute.split("-");
    const originId = parts[0] ? parseInt(parts[0], 10) : NaN;
    return Number.isFinite(originId) ? originId : null;
  }, [selectedTariff, selectedRoute]);

  const destinationPortId = useMemo(() => {
    if (selectedTariff?.destination_port?.id)
      return selectedTariff.destination_port.id;
    if (!selectedRoute) return null;
    const parts = selectedRoute.split("-");
    const destId = parts[1] ? parseInt(parts[1], 10) : NaN;
    return Number.isFinite(destId) ? destId : null;
  }, [selectedTariff, selectedRoute]);

  const isSeafreightFcl =
    transportTypeId === 2 && bookingType === "FCL" && incotermId != null;
  const isSeafreightLcl =
    transportTypeId === 2 && bookingType === "LCL";
  const isAirfreight = transportTypeId === 1;

  const deliveryDestinationForDistance = useMemo(
    () =>
      deliveryType === "delivery" && deliveryAddress
        ? {
            type: "coordinates" as const,
            lat: deliveryAddress.lat,
            lon: deliveryAddress.lon,
            formatted: deliveryAddress.formatted,
          }
        : null,
    [deliveryType, deliveryAddress?.lat, deliveryAddress?.lon, deliveryAddress?.formatted],
  );

  const {
    distanceKm,
    unknownKm,
    routeGeometry,
    portName,
    destLabel,
    portCoords,
    destCoords,
  } = usePortToAddressDistance({
      destinationPortId,
      deliveryType,
      deliveryDestination: deliveryDestinationForDistance,
    });

  const containervervoerResult = useMemo(() => {
    if (!isSeafreightFcl) return null;
    if (unknownKm) return { unknownKm: true as const, sellingPrice: 0, basePrice: 0, calculationFormula: null, distanceKm: undefined };
    if (distanceKm == null) return null;
    const { basePrice, sellingPrice, calculationFormula } = getContainervervoerPrice(distanceKm);
    return { unknownKm: false as const, sellingPrice, basePrice, distanceKm, calculationFormula };
  }, [isSeafreightFcl, unknownKm, distanceKm]);

  const warehouseDeliveryRoute = useWarehouseToAddressRoute({
    deliveryAddress:
      (isSeafreightLcl || isAirfreight) && deliveryType === "delivery"
        ? deliveryAddress
        : null,
  });

  const displayTotal =
    totalCost + (containervervoerResult?.sellingPrice ?? 0);

  // Pallets: same as full calculator — 1.6 CBM rule when cartons >= palletThreshold or forcePalletShipment.
  // Volume 0 still bills at least 1 pallet in pallet mode (LMD / outbound need a tier).
  const calculatedPallets = useMemo(() => {
    if (cartons < palletThreshold && !forcePalletShipment) {
      return 0;
    }
    return Math.max(1, Math.ceil(volume / CBM_PER_PALLET) || 1);
  }, [cartons, volume, forcePalletShipment, palletThreshold]);

  const actualPallets =
    overridePallets !== null ? overridePallets : calculatedPallets;

  // Debounced pricing — avoids stale LMD results when fields change in quick succession
  useEffect(() => {
    if (auxDataLoading) return;

    const requestId = nextPricingRequest();
    const timer = window.setTimeout(() => {
      void (async () => {
        if (!isCurrentPricingRequest(requestId)) return;

        if (
          !transportTypeId ||
          !selectedRoute ||
          !incotermId ||
          !originPortId ||
          !destinationPortId
        ) {
          if (isCurrentPricingRequest(requestId)) {
            setTotalCost(0);
            setHasError(null);
            setPricingTemplatesResult(null);
          }
          return;
        }

        try {
          if (isCurrentPricingRequest(requestId)) setHasError(null);

          let tariffToUse = selectedTariff;
          if (!tariffToUse && originPortId && destinationPortId) {
            const routeTariffs = tariffs.filter(
              (t) =>
                t.transport_type?.id === transportTypeId &&
                t.origin_port?.id === originPortId &&
                t.destination_port?.id === destinationPortId &&
                (!t.to || dayjs(t.to).isAfter(dayjs())),
            );

            if (transportTypeId === 2 && bookingType) {
              const expectedBookingsTypeId = bookingType === "FCL" ? 2 : 1;
              const filtered = routeTariffs.filter(
                (t) => t.bookings_type?.id === expectedBookingsTypeId,
              );
              if (filtered.length > 0) {
                const sorted = [...filtered].sort((a, b) => {
                  if (!a.from) return 1;
                  if (!b.from) return -1;
                  return dayjs(b.from).valueOf() - dayjs(a.from).valueOf();
                });
                tariffToUse = sorted[0];
              }
            } else if (transportTypeId === 1) {
              const sorted = [...routeTariffs].sort((a, b) => {
                if (!a.from) return 1;
                if (!b.from) return -1;
                return dayjs(b.from).valueOf() - dayjs(a.from).valueOf();
              });
              tariffToUse = sorted[0];
            }
          }

          const pricingContext: PricingContext = {
            transportTypeId,
            bookingTypeId:
              transportTypeId === 2
                ? bookingType === "LCL"
                  ? 1
                  : bookingType === "FCL"
                    ? 2
                    : null
                : undefined,
            incotermId,
            originPortId: originPortId!,
            destinationPortId: destinationPortId!,
            containerSize: tariffToUse?.container_size?.id ?? undefined,
            matchDate: expectedDepartureDate.format("YYYY-MM-DD"),
          };

          const bookingData: BookingData = {
            totalCartons: cartons,
            totalPallets: actualPallets,
            uniqueProducts: hsCodes,
            totalWeight: weight,
            totalVolume: volume,
            uniqueHsCodesCount: hsCodes,
            totalValue: totalValue > 0 ? totalValue : undefined,
            containerSize: tariffToUse?.container_size?.id ?? undefined,
          };

          const selectedContainerSizeName =
            containerSize != null
              ? containerSizes.find((s) => Number(s.id) === Number(containerSize))
                  ?.name
              : undefined;
          const result = await calculatePricingFromTemplates(
            pricingContext,
            bookingData,
            {
              selectedTariff: tariffToUse,
              containerSizeName: selectedContainerSizeName,
              isStorageDelivery: deliveryType === "storage",
              formatEuro,
              t: (key: string, defaultValue?: string) => t(key, defaultValue),
              filterZeroComponents: true,
              deliveryType,
              lmdCountryCode:
                deliveryType === "delivery" && lmdCountry
                  ? lmdCountry
                  : undefined,
              destinationCountryCode:
                tariffToUse?.destination_port?.country ?? undefined,
              palletThreshold,
              selectedAdditionalCostIds: Array.from(selectedAdditionalCosts),
              additionalCostOverrides: additionalCostValues,
              pickupFee: pickupFee > 0 ? pickupFee : undefined,
              europalletsExchanged:
                cartons >= palletThreshold || forcePalletShipment
                  ? europalletsExchanged
                  : true,
            },
          );

          if (!isCurrentPricingRequest(requestId)) return;

          if (result.error) {
            setHasError(result.error);
            setTotalCost(0);
            setPricingTemplatesResult(null);
            return;
          }

          setTotalCost(result.totalCost);
          setPricingTemplatesResult({
            totalCost: result.totalCost,
            components: result.components.map((comp) => ({
              name: comp.name,
              totalPrice: comp.totalPrice,
              unitPrice: comp.unitPrice ?? comp.totalPrice,
              quantity: comp.quantity ?? 1,
              calculationType: comp.calculationType,
              calculationDetails: comp.calculationDetails,
              componentType: comp.componentType,
              componentId: comp.componentId,
            })),
            matchedTemplates: result.matchedTemplates,
            matchingCriteria: result.matchingCriteria,
            warnings: result.warnings ?? [],
          });
        } catch (error) {
          if (isCurrentPricingRequest(requestId)) {
            console.error("Error calculating pricing:", error);
            setHasError("Calculation error");
            setTotalCost(0);
            setPricingTemplatesResult(null);
          }
        }
      })();
    }, PRICING_CALC_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [
    auxDataLoading,
    transportTypeId,
    bookingType,
    incotermId,
    selectedRoute,
    weight,
    volume,
    cartons,
    actualPallets,
    totalValue,
    hsCodes,
    selectedTariff,
    originPortId,
    destinationPortId,
    lmdCountry,
    palletThreshold,
    europalletsExchanged,
    deliveryType,
    pickupFee,
    selectedAdditionalCostsKey,
    additionalCostValuesKey,
    tariffs,
    expectedDepartureDate,
    forcePalletShipment,
    containerSize,
    containerSizes,
    nextPricingRequest,
    isCurrentPricingRequest,
  ]);

  const handleReset = () => {
    setTransportTypeId(null);
    setBookingType(null);
    setIncotermId(null);
    setSelectedRoute(null);
    setExpectedDepartureDate(dayjs().add(7, "day"));
    setWeight(1);
    setVolume(1);
    setCartons(1);
    setHsCodes(1);
    setTotalValue(0);
    setContainerSize(null);
    setPickupFee(0);
    setDeliveryType("delivery");
    setDeliveryAddress(null);
    setLmdCountry("NL");
    setOverridePallets(null);
    setForcePalletShipment(false);
    setEuropalletsExchanged(true);
    setPricingTemplatesResult(null);
    setSelectedAdditionalCosts(new Set());
    setAdditionalCostValues({});
    if (!isPopup && !isCompact) {
      trackFeatureUsage("tools_calculator_reset");
    }
  };

  const hasValidCalculation =
    transportTypeId &&
    selectedRoute &&
    incotermId &&
    originPortId &&
    destinationPortId;

  const applicableCountryCodes = useMemo(() => {
    const codes = new Set<string>();
    const deliveryCode = normalizeCountryCode(lmdCountry);
    if (deliveryCode) {
      codes.add(deliveryCode);
    }

    const destinationCountry = selectedTariff?.destination_port?.country;
    const destinationCode = normalizeCountryCode(destinationCountry);
    if (destinationCode) {
      codes.add(destinationCode);
    }

    return Array.from(codes);
  }, [lmdCountry, selectedTariff]);

  const lmdCountriesForSelect = useMemo(
    () =>
      availableLmdCountries.length > 0 ? availableLmdCountries : ["NL", "BE"],
    [availableLmdCountries],
  );

  const filteredAdditionalCosts = useMemo(() => {
    if (availableAdditionalCosts.length === 0) {
      return [];
    }

    if (applicableCountryCodes.length === 0) {
      return availableAdditionalCosts;
    }

    const filtered = availableAdditionalCosts.filter((cost) => {
      const costNameLower = cost.name.toLowerCase();
      const isExportClearance =
        costNameLower.includes("export") && costNameLower.includes("clearance");

      if (isExportClearance && incotermId === 3) {
        return false; // Don't show export clearance for FOB
      }

      if (
        !cost.applies_to_countries ||
        cost.applies_to_countries.length === 0
      ) {
        return true;
      }

      const matchesCountry = cost.applies_to_countries
        .map((code) => normalizeCountryCode(code as string))
        .some((code) => code && applicableCountryCodes.includes(code));

      if (!matchesCountry) {
        return false;
      }

      if (cost.applies_to_incoterms && cost.applies_to_incoterms.length > 0) {
        if (!incotermId) {
          return false;
        }
        const normalizedIncoterms = cost.applies_to_incoterms.map((id) =>
          typeof id === "string" ? parseInt(id, 10) : id,
        );
        if (!normalizedIncoterms.includes(incotermId)) {
          return false;
        }
      }

      return true;
    });

    return filtered;
  }, [availableAdditionalCosts, applicableCountryCodes, incotermId]);

  const hasPickupCostInAdditional =
    filteredAdditionalCosts.some((cost) => {
      const nameLower = (cost.name || "").toLowerCase();
      return nameLower.includes("ophaal") || nameLower.includes("pickup");
    });

  // Build URL for full calculator (must match params used by Calculator page URL sync)
  const buildFullCalculatorUrl = () => {
    const params = new URLSearchParams();
    if (transportTypeId)
      params.set("transportTypeId", transportTypeId.toString());
    if (bookingType) params.set("bookingType", bookingType);
    if (incotermId) params.set("incotermId", incotermId.toString());
    if (selectedRoute) params.set("route", selectedRoute);
    if (expectedDepartureDate)
      params.set(
        "expectedDepartureDate",
        expectedDepartureDate.format("YYYY-MM-DD"),
      );
    if (weight !== 1) params.set("weight", weight.toString());
    if (volume !== 1) params.set("volume", volume.toString());
    if (cartons !== 1) params.set("cartons", cartons.toString());
    if (hsCodes !== 1) params.set("hsCodes", hsCodes.toString());
    if (deliveryType) params.set("deliveryType", deliveryType);
    if (lmdCountry) params.set("lmdCountry", lmdCountry);
    if (containerSize) params.set("containerSize", containerSize.toString());
    if (pickupFee > 0) params.set("pickupFee", pickupFee.toString());
    if (selectedAdditionalCosts.size > 0) {
      params.set(
        "additionalCosts",
        Array.from(selectedAdditionalCosts).join(","),
      );
    }
    if (Object.keys(additionalCostValues).length > 0) {
      params.set("additionalCostValues", JSON.stringify(additionalCostValues));
    }

    return `/tools/calculator${params.toString() ? `?${params.toString()}` : ""}`;
  };

  const handleNavigateToFull = () => {
    if (onNavigateToFull) {
      onNavigateToFull();
    } else {
      navigate(buildFullCalculatorUrl());
    }
  };

  const isCalculatorDataLoading =
    loadingTariffs ||
    loadingTransportTypes ||
    loadingIncoterms ||
    auxDataLoading;

  // Show loading until routes, containers, incoterms etc. are loaded (avoids ID flash)
  if (isCalculatorDataLoading) {
    return (
      <CompactCard
        title={t("tools.calculator.title", "Calculator")}
        bodyStyle={{ padding: "48px 24px" }}
      >
        <div style={{ textAlign: "center" }}>
          <Spin size="large" tip={t("common.loading", "Loading...")} />
        </div>
      </CompactCard>
    );
  }

  // Render compact version
  if (isCompact) {
    return (
      <CompactCard
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{t("tools.calculator.title", "Calculator")}</span>
            {showFullButton && (
              <Button
                type="link"
                size="small"
                icon={<CalculatorOutlined />}
                onClick={handleNavigateToFull}
                style={{ padding: 0, height: "auto" }}
              >
                {t("tools.calculator.openCalculator", "Open calculator")}
              </Button>
            )}
          </div>
        }
      >
        <TotalDisplay>
          <TotalLabel>
            {t("tools.calculator.totalEstimatedCost", "Total Estimated Cost")}
          </TotalLabel>
          <TotalValue>
            {hasValidCalculation ? formatEuro(displayTotal) : "€0.00"}
          </TotalValue>
        </TotalDisplay>

        {hasError && (
          <Alert
            message={hasError}
            type="warning"
            style={{ marginBottom: 12 }}
            showIcon
            size="small"
          />
        )}

        {isTariffFallback && (
          <Alert
            message={t("tools.calculator.tariffFallbackWarning")}
            type="info"
            style={{ marginBottom: 12 }}
            showIcon
            size="small"
          />
        )}

        <Row gutter={[12, 12]}>
          {/* Column 1 - Transport, Route, Incoterm, Weight/Vol/Cartons */}
          <Col xs={24} lg={8}>
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <CompactInputGroup>
                <CompactLabel>
                  {t("tools.calculator.transportType", "Transport Type")}
                </CompactLabel>
                <Select
                  placeholder={t(
                    "tools.calculator.selectTransportType",
                    "Select transport type",
                  )}
                  value={transportTypeId}
                  onChange={setTransportTypeId}
                  size="small"
                >
                  {transportTypes.map((type) => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </CompactInputGroup>

              {transportTypeId === 2 && (
                <CompactInputGroup>
                  <CompactLabel>
                    {t("tools.calculator.bookingType", "Booking Type")}
                  </CompactLabel>
                  <Row gutter={8}>
                    <Col span={12}>
                      <Button
                        size="small"
                        type={bookingType === "LCL" ? "primary" : "default"}
                        onClick={() => setBookingType("LCL")}
                        style={{ width: "100%" }}
                      >
                        LCL
                      </Button>
                    </Col>
                    <Col span={12}>
                      <Button
                        size="small"
                        type={bookingType === "FCL" ? "primary" : "default"}
                        onClick={() => setBookingType("FCL")}
                        style={{ width: "100%" }}
                      >
                        FCL
                      </Button>
                    </Col>
                  </Row>
                </CompactInputGroup>
              )}

              {transportTypeId === 2 && bookingType === "FCL" && (
                <CompactInputGroup>
                  <CompactLabel>
                    {t("tools.calculator.containerSize", "Container Grootte")}
                  </CompactLabel>
                  <Select
                    value={containerSize}
                    onChange={(val) => setContainerSize(val ?? null)}
                    placeholder={t(
                      "tools.calculator.selectContainerSize",
                      "Select container size",
                    )}
                    size="small"
                    style={{ width: "100%" }}
                    allowClear
                  >
                    {containerSizes.map((size) => (
                      <Option key={size.id} value={size.id}>
                        {size.name}
                      </Option>
                    ))}
                  </Select>
                </CompactInputGroup>
              )}

              {transportTypeId && (transportTypeId !== 2 || bookingType) && (
                <CompactInputGroup>
                  <CompactLabel>
                    {t("tools.calculator.expectedDepartureDate", "Verwachtte vertrekdatum")}
                  </CompactLabel>
                  <DatePicker
                    value={expectedDepartureDate}
                    onChange={(d) =>
                      setExpectedDepartureDate(d || dayjs().add(7, "day"))
                    }
                    disabledDate={(current) =>
                      current ? current.isBefore(dayjs(), "day") : false
                    }
                    format="YYYY-MM-DD"
                    size="small"
                    style={{ width: "100%" }}
                  />
                  <Text
                    style={{
                      fontSize: 10,
                      lineHeight: 1.35,
                      display: "block",
                      marginTop: 4,
                      color: "#d4380d",
                      fontWeight: 500,
                    }}
                  >
                    {t("tools.calculator.expectedDepartureDateHelp")}
                  </Text>
                </CompactInputGroup>
              )}

              {transportTypeId &&
                (transportTypeId !== 2 || bookingType) &&
                (transportTypeId !== 2 ||
                  bookingType !== "FCL" ||
                  containerSize != null) && (
                <CompactInputGroup>
                  <CompactLabel>
                    {t("tools.calculator.route", "Route")}
                  </CompactLabel>
                  <Select
                    placeholder={t(
                      "tools.calculator.selectRoute",
                      "Select route",
                    )}
                    value={selectedRoute}
                    onChange={setSelectedRoute}
                    size="small"
                    loading={routeOptions.length === 0}
                  >
                    {routeOptions.map((route) => (
                      <Option
                        key={route.key || route.value}
                        value={route.value}
                      >
                        {route.label}
                      </Option>
                    ))}
                  </Select>
                  {selectedTariff && (
                    <div style={{ marginTop: 6 }}>
                      <Text type="secondary" style={{ fontSize: 10, display: "block", marginBottom: 2 }}>
                        {t("tools.calculator.validityPeriod", "Validity period:")}{" "}
                        {selectedTariff.from && selectedTariff.to ? (
                          <>
                            {dayjs(selectedTariff.from).format("DD MMM YYYY")} - {dayjs(selectedTariff.to).format("DD MMM YYYY")}
                          </>
                        ) : selectedTariff.from ? (
                          <>
                            {t("tools.calculator.from", "From")} {dayjs(selectedTariff.from).format("DD MMM YYYY")}
                          </>
                        ) : selectedTariff.to ? (
                          <>
                            {t("tools.calculator.until", "Until")} {dayjs(selectedTariff.to).format("DD MMM YYYY")}
                          </>
                        ) : (
                          t("tools.calculator.alwaysValid", "Always valid")
                        )}
                      </Text>
                      {bookingType === "LCL" && selectedTariff.tariff_bracket && (
                        <Text type="secondary" style={{ fontSize: 10, display: "block", marginBottom: 2 }}>
                          {t("tools.calculator.bracket", "Bracket")}: {selectedTariff.tariff_bracket.from}–{selectedTariff.tariff_bracket.to}{" "}
                          {t("tools.calculator.cbm", "CBM")} → €{selectedTariff.price?.toFixed(2) ?? "0.00"}/CBM
                        </Text>
                      )}
                      {bookingType === "FCL" && transportTypeId === 2 && selectedTariff.container_size && (
                        <Text type="secondary" style={{ fontSize: 10, display: "block", marginBottom: 2 }}>
                          {t("tools.calculator.containerLabel", "Container")}: {selectedTariff.container_size.name} → €
                          {selectedTariff.price?.toFixed(2) ?? "0.00"}/{t("tools.calculator.container", "container")}
                        </Text>
                      )}
                      <Text type="secondary" style={{ fontSize: 10, lineHeight: 1.3, display: "block" }}>
                        {t(
                          "tools.calculator.etdExplanation",
                          "The prices shown are based on the validity period of the selected tariff above. This tariff only applies to shipments within that period. If your shipment's departure date (ETD) falls outside this period, a different tariff may apply.",
                        )}
                      </Text>
                    </div>
                  )}
                </CompactInputGroup>
              )}

              {transportTypeId && (transportTypeId !== 2 || bookingType) && (
                <CompactInputGroup>
                  <CompactLabel>
                    {t("tools.calculator.incoterm", "Incoterm")}
                  </CompactLabel>
                  <Select
                    placeholder={t(
                      "tools.calculator.selectIncoterm",
                      "Select incoterm",
                    )}
                    value={incotermId}
                    onChange={setIncotermId}
                    size="small"
                  >
                    {incoterms.map((incoterm) => (
                      <Option key={incoterm.id} value={incoterm.id}>
                        {incoterm.name}
                      </Option>
                    ))}
                  </Select>
                </CompactInputGroup>
              )}

              {hasValidCalculation && (
                <>
                  <Row gutter={8}>
                    <Col span={12}>
                      <CompactInputGroup>
                        <CompactLabel>
                          {t("tools.calculator.weight", "Weight (kg)")}
                        </CompactLabel>
                        <InputNumber
                          value={weight}
                          onChange={(val) => setWeight(val || 1)}
                          min={0.01}
                          step={0.1}
                          size="small"
                        />
                      </CompactInputGroup>
                    </Col>
                    <Col span={12}>
                      <CompactInputGroup>
                        <CompactLabel>
                          {t("tools.calculator.volume", "Volume (CBM)")}
                        </CompactLabel>
                        <InputNumber
                          value={volume}
                          onChange={(val) => {
                            const n = Number(val);
                            setVolume(
                              Number.isFinite(n) && n > 0 ? n : 0.01,
                            );
                          }}
                          min={0.01}
                          step={0.01}
                          size="small"
                        />
                      </CompactInputGroup>
                    </Col>
                  </Row>

                  <CompactInputGroup>
                    <CompactLabel>
                      {t("tools.calculator.cartons", "Cartons")}
                    </CompactLabel>
                    <InputNumber
                      value={cartons}
                      onChange={(val) => setCartons(val || 1)}
                      min={1}
                      step={1}
                      size="small"
                    />
                  </CompactInputGroup>

                  {cartons < palletThreshold && (
                    <CompactInputGroup>
                      <Checkbox
                        checked={forcePalletShipment}
                        onChange={(e) =>
                          setForcePalletShipment(e.target.checked)
                        }
                      >
                        {t(
                          "tools.calculator.forcePalletShipment",
                          "Force pallet shipment",
                        )}
                      </Checkbox>
                    </CompactInputGroup>
                  )}

                  {(cartons >= palletThreshold || forcePalletShipment) && (
                    <>
                      <CompactInputGroup>
                        <CompactLabel>
                          {t("tools.calculator.pallets", "Pallets")}
                        </CompactLabel>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <InputNumber
                            value={
                              overridePallets !== null
                                ? overridePallets
                                : calculatedPallets
                            }
                            onChange={(val) =>
                              setOverridePallets(val ?? calculatedPallets)
                            }
                            min={0}
                            step={1}
                            controls={true}
                            size="small"
                            style={{ flex: 1, minWidth: 0 }}
                            placeholder={`Auto: ${calculatedPallets}`}
                            disabled={overridePallets === null}
                          />
                          {overridePallets === null ? (
                            <Button
                              type="text"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() =>
                                setOverridePallets(calculatedPallets)
                              }
                              title={t(
                                "tools.calculator.editPalletsOverride",
                                "Edit pallets",
                              )}
                            />
                          ) : (
                            <Button
                              type="link"
                              size="small"
                              onClick={() => setOverridePallets(null)}
                              style={{ padding: 0 }}
                            >
                              {t("tools.calculator.resetToAuto", "Reset")}
                            </Button>
                          )}
                        </div>
                      </CompactInputGroup>
                      <CompactInputGroup>
                        <Checkbox
                          checked={europalletsExchanged}
                          onChange={(e) =>
                            setEuropalletsExchanged(e.target.checked)
                          }
                        >
                          {europalletsExchanged
                            ? t(
                                "tools.calculator.europalletsExchangedYes",
                                "Europallets geruild (geen kosten)",
                              )
                            : t(
                                "tools.calculator.europalletsExchangedNo",
                                "Europallets niet geruild (€15/pallet)",
                              )}
                        </Checkbox>
                      </CompactInputGroup>
                    </>
                  )}
                </>
              )}

              {(transportTypeId || selectedRoute || incotermId) && (
                <Button
                  size="small"
                  icon={<UndoOutlined />}
                  onClick={handleReset}
                  style={{ width: "100%" }}
                >
                  {t("tools.calculator.reset", "Reset")}
                </Button>
              )}
            </Space>
          </Col>

          {/* Column 2 - HS Codes, Delivery Type, LMD Country, Additional Costs */}
          <Col xs={24} lg={8}>
            {(incotermId || deliveryType === "delivery") && (
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                {incotermId && (
                  <CompactInputGroup>
                    <CompactLabel>
                      {t("tools.calculator.hsCodes", "HS Codes")}
                    </CompactLabel>
                    <InputNumber
                      value={hsCodes}
                      onChange={(val) => setHsCodes(val || 1)}
                      min={1}
                      step={1}
                      size="small"
                    />
                  </CompactInputGroup>
                )}

                <CompactInputGroup>
                  <CompactLabel>
                    {t("tools.calculator.deliveryType", "Delivery Type")}
                  </CompactLabel>
                  <Select
                    value={deliveryType}
                    onChange={(val) => {
                      if (val === "delivery" || val === "storage") {
                        setDeliveryType(val);
                        if (val === "storage") setDeliveryAddress(null);
                      }
                    }}
                    size="small"
                  >
                    <Option value="delivery">
                      {t("tools.calculator.delivery", "Delivery")}
                    </Option>
                    <Option value="storage">
                      {t("tools.calculator.storage", "Storage")}
                    </Option>
                  </Select>
                </CompactInputGroup>

                {deliveryType === "delivery" && (
                  <>
                    {(isSeafreightFcl || isSeafreightLcl || isAirfreight) && (
                      <CompactInputGroup>
                        <CompactLabel>
                          {t(
                            "tools.calculator.deliveryAddress",
                            "Delivery Address",
                          )}
                        </CompactLabel>
                        <DeliveryAddressAutocomplete
                          value={deliveryAddress?.formatted}
                          onChange={(p) =>
                            setDeliveryAddress(
                              p ? { lat: p.lat, lon: p.lon, formatted: p.formatted } : null,
                            )
                          }
                          placeholder={t(
                            "tools.calculator.enterDeliveryAddress",
                            "Enter address for distance calculation",
                          )}
                          size="small"
                        />
                      </CompactInputGroup>
                    )}
                    <CompactInputGroup>
                      <CompactLabel>
                        {t("tools.calculator.lmdCountry", "Delivery Country")}
                      </CompactLabel>
                      <Select
                        value={
                          lmdCountriesForSelect.includes(lmdCountry)
                            ? lmdCountry
                            : lmdCountriesForSelect[0]
                        }
                        onChange={(val) => {
                          if (
                            val &&
                            typeof val === "string" &&
                            lmdCountriesForSelect.includes(val)
                          ) {
                            setLmdCountry(val);
                          }
                        }}
                        size="small"
                      >
                        {lmdCountriesForSelect.map((country) => (
                          <Option key={country} value={country}>
                            {country}
                          </Option>
                        ))}
                      </Select>
                    </CompactInputGroup>
                  </>
                )}

                {isSeafreightFcl && (containervervoerResult != null || (portCoords && destCoords)) && (
                  <ContainervervoerCalculationInfo
                    portName={portName}
                    destLabel={destLabel}
                    distanceKm={distanceKm}
                    unknownKm={containervervoerResult?.unknownKm ?? false}
                    sellingPrice={containervervoerResult?.sellingPrice ?? 0}
                    calculationFormula={containervervoerResult?.calculationFormula ?? null}
                    portCoords={portCoords}
                    destCoords={destCoords}
                    routeGeometry={routeGeometry}
                    routeReady={
                      deliveryType === "storage" ||
                      (deliveryType === "delivery" && deliveryAddress != null)
                    }
                    preloadedMapSrc={
                      deliveryType === "storage" && destinationPortId
                        ? PRELOADED_MAP_PATHS[destinationPortId] ?? null
                        : null
                    }
                  />
                )}

                {(isSeafreightLcl || isAirfreight) &&
                  deliveryType === "delivery" &&
                  deliveryAddress && (
                    <WarehouseToAddressMap
                      destLabel={warehouseDeliveryRoute.destLabel}
                      distanceKm={warehouseDeliveryRoute.distanceKm}
                      destCoords={warehouseDeliveryRoute.destCoords}
                      routeGeometry={warehouseDeliveryRoute.routeGeometry}
                      loading={warehouseDeliveryRoute.loading}
                      error={warehouseDeliveryRoute.error}
                    />
                  )}

                <Divider style={{ margin: "6px 0" }}>
                  <Text style={{ fontSize: 11 }}>
                    {t("tools.calculator.additionalCosts", "Additional Costs")}
                  </Text>
                </Divider>

                <div style={{ marginBottom: 6 }}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 10, lineHeight: "1.35" }}
                  >
                    {t(
                      "tools.calculator.additionalCostsExplanation",
                      "Additional costs, if applicable",
                    )}
                  </Text>
                </div>

                {filteredAdditionalCosts.length > 0 ? (
                  <>
                    <Space
                      direction="vertical"
                      size="small"
                      style={{ width: "100%" }}
                    >
                      {filteredAdditionalCosts.map((cost) => {
                        const defaultValue =
                          parseNumericValue(cost.default_value) ??
                          parseNumericValue(cost.cost_eur) ??
                          0;
                        const overrideValue = additionalCostValues[cost.id];
                        const amountPreview = overrideValue ?? defaultValue ?? 0;
                        return (
                          <div key={cost.id}>
                            <Checkbox
                              checked={selectedAdditionalCosts.has(cost.id)}
                              onChange={(e) => {
                                const newSelected = new Set(
                                  selectedAdditionalCosts,
                                );
                                if (e.target.checked) {
                                  newSelected.add(cost.id);
                                } else {
                                  newSelected.delete(cost.id);
                                }
                                setSelectedAdditionalCosts(newSelected);
                              }}
                            >
                              <Text style={{ fontSize: 11 }}>{cost.name}</Text>
                            </Checkbox>
                            {selectedAdditionalCosts.has(cost.id) &&
                              cost.calculation_type !== "fixed" && (
                                <div style={{ marginTop: 2, marginLeft: 24 }}>
                                  <InputNumber
                                    size="small"
                                    min={0}
                                    step={
                                      cost.calculation_type === "percentage"
                                        ? 0.1
                                        : 1
                                    }
                                    value={overrideValue ?? undefined}
                                    placeholder={
                                      defaultValue !== null
                                        ? defaultValue.toString()
                                        : undefined
                                    }
                                    onChange={(val) =>
                                      handleAdditionalCostValueChange(
                                        cost.id,
                                        val ?? null,
                                      )
                                    }
                                    style={{ width: 120 }}
                                  />
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 10, marginLeft: 8 }}
                                  >
                                    {formatEuro(amountPreview)}
                                  </Text>
                                </div>
                              )}
                          </div>
                        );
                      })}
                    </Space>
                    {hasPickupCostInAdditional && (
                      <Text
                        type="secondary"
                        style={{ fontSize: 10, display: "block", marginTop: 8, lineHeight: 1.35 }}
                      >
                        {t("tools.calculator.pickupCostsOnRequest")}
                        <a href="mailto:info@efreightforwarding.com">info@efreightforwarding.com</a>.
                      </Text>
                    )}
                  </>
                ) : (
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    {applicableCountryCodes.length === 0
                      ? t(
                          "tools.calculator.selectRouteOrCountryForCosts",
                          "Select a route or delivery country first to see relevant additional costs.",
                        )
                      : t(
                          "tools.calculator.noAdditionalCosts",
                          "No additional costs available for this country selection.",
                        )}
                  </Text>
                )}
              </Space>
            )}
          </Col>

          {/* Column 3 - Cost Overview (Kostenoverzicht) - include all components (transport, LMD, etc.) */}
          <Col xs={24} lg={8}>
            {hasValidCalculation && pricingTemplatesResult ? (
              <Card
                size="small"
                title={t("tools.calculator.breakdown", "Cost Overview")}
                bodyStyle={{ padding: "8px 12px" }}
              >
                <Space direction="vertical" style={{ width: "100%" }} size={4}>
                  {pricingTemplatesResult.warnings.length > 0 && (
                    <Alert
                      type="warning"
                      showIcon
                      message={t(
                        "tools.calculator.lmdPricingWarningTitle",
                        "Last mile delivery is missing from this breakdown",
                      )}
                      description={
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {pricingTemplatesResult.warnings.map((w, i) => (
                            <li key={i} style={{ fontSize: 11 }}>
                              {t(w.key, {
                                ...w.params,
                                defaultValue:
                                  w.key === "tools.calculator.lmdRateNotFound"
                                    ? "Last-mile delivery ({{country}}) is not included: No LMD rate found for {{country}} with {{pallets}} pallets or {{cartons}} cartons"
                                    : "Last-mile delivery ({{country}}) could not be calculated",
                              })}
                            </li>
                          ))}
                        </ul>
                      }
                    />
                  )}
                  {[
                    ...pricingTemplatesResult.components,
                    ...(containervervoerResult != null
                      ? [
                          {
                            name: "Containervervoer plaats van bestemming",
                            totalPrice: containervervoerResult.sellingPrice,
                            calculationDetails: containervervoerResult.unknownKm
                              ? t("tools.calculator.unknownKm", "Onbekende kilometers")
                              : (containervervoerResult.calculationFormula ?? `${(containervervoerResult as { distanceKm?: number }).distanceKm?.toFixed(0) ?? 0} km`),
                            componentType: "containervervoer_plaats_van_bestemming",
                          },
                        ]
                      : []),
                  ]
                    .sort(sortCostOverview)
                    .map((component, index) => {
                    const isTransportComponent =
                      component.name.toLowerCase().includes("transport") ||
                      component.name.toLowerCase().includes("freight");
                    const showVolumetricWeight =
                      isTransportComponent &&
                      transportTypeId === 1 &&
                      weight > 0 &&
                      volume > 0;

                    return (
                      <div key={index} style={{ marginBottom: 6 }}>
                        <Row
                          justify="space-between"
                          style={{ marginBottom: 2 }}
                        >
                          <Text strong style={{ fontSize: 12 }}>
                            {component.name}
                          </Text>
                          <Text strong style={{ fontSize: 12 }}>
                            {formatEuro(component.totalPrice)}
                          </Text>
                        </Row>
                        {component.calculationDetails && (
                          <Row>
                            <Text type="secondary" style={{ fontSize: 10 }}>
                              {showVolumetricWeight &&
                              component.calculationDetails.includes("kg ×") ? (
                                component.calculationDetails
                              ) : component.calculationDetails ===
                                  `1 × ${formatEuro(component.totalPrice)}` ? (
                                // Fixed fee: "1 × € X" — skip redundant "= € X"
                                component.calculationDetails
                              ) : (
                                <>
                                  {component.calculationDetails} ={" "}
                                  {formatEuro(component.totalPrice)}
                                </>
                              )}
                            </Text>
                          </Row>
                        )}
                      </div>
                    );
                  })}
                  <Divider style={{ margin: "6px 0" }} />
                  <Row justify="space-between">
                    <Text strong style={{ fontSize: 13 }}>
                      {t("tools.calculator.total", "Total")}
                    </Text>
                    <Text strong style={{ fontSize: 13 }}>
                      {formatEuro(
                        pricingTemplatesResult.totalCost +
                          (containervervoerResult?.sellingPrice ?? 0),
                      )}
                    </Text>
                  </Row>
                </Space>
              </Card>
            ) : (
              <Card size="small" bodyStyle={{ padding: "12px" }}>
                <Text
                  type="secondary"
                  style={{
                    textAlign: "center",
                    display: "block",
                    padding: "4px 0",
                    fontSize: 11,
                  }}
                >
                  {t(
                    "tools.calculator.fillFieldsToSeeBreakdown",
                    "Fill in the fields to see cost breakdown",
                  )}
                </Text>
              </Card>
            )}
          </Col>
        </Row>
      </CompactCard>
    );
  }

  // Render full or popup version (same layout, different header)
  return (
    <div style={{ padding: isPopup ? "0" : "8px 0 16px", width: "100%" }}>
      <Space direction="vertical" size={4} style={{ width: "100%" }}>
        {showHeader && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "4px 12px",
              marginBottom: 0,
            }}
          >
            {!isPopup && (
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() => (onClose ? onClose() : navigate("/tools"))}
                style={{ padding: 0, height: "auto", fontSize: 13 }}
              >
                {t("common.back")}
              </Button>
            )}
            {isPopup && onClose && (
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={onClose}
                style={{ padding: 0, height: "auto", fontSize: 13 }}
              >
                {t("common.close", "Close")}
              </Button>
            )}
            <Title level={3} style={{ margin: 0, fontSize: 18 }}>
              {t("tools.calculator.title")}
            </Title>
            {!isPopup && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("tools.calculator.description")}
              </Text>
            )}
            <div style={{ marginLeft: "auto" }}>
              {(transportTypeId || selectedRoute || incotermId) && (
                <Button
                  size="small"
                  icon={<UndoOutlined />}
                  onClick={handleReset}
                  title={t("tools.calculator.reset", "Reset")}
                >
                  {t("tools.calculator.reset", "Reset")}
                </Button>
              )}
            </div>
          </div>
        )}

        <CalculatorContainer>
          {/* Calculator Display */}
          <Card
            size="small"
            style={{ marginBottom: 8, textAlign: "center" }}
            bodyStyle={{ padding: "8px 12px" }}
          >
            <DisplayLabel>
              {t("tools.calculator.totalEstimatedCost")}
            </DisplayLabel>
            <DisplayValue>
              {hasValidCalculation ? formatEuro(displayTotal) : "€0.00"}
            </DisplayValue>
          </Card>

          {/* Error Message */}
          {hasError && (
            <Alert
              message={
                <Space size={4}>
                  <Text style={{ fontSize: 11 }}>{hasError}</Text>
                </Space>
              }
              type="warning"
              style={{ marginBottom: 8 }}
              showIcon
            />
          )}

          {/* Full calculator content would continue here - this is a simplified version */}
          {/* For now, we'll keep the compact version working and you can expand the full version later */}
          <Row gutter={12}>
            <Col xs={24} lg={8}>
              <Card size="small" bodyStyle={{ padding: "8px 12px" }}>
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <InputGroup>
                    <Label>{t("tools.calculator.transportType")}</Label>
                    <Select
                      placeholder={t("tools.calculator.selectTransportType")}
                      value={transportTypeId}
                      onChange={setTransportTypeId}
                      style={{ width: "100%" }}
                    >
                      {transportTypes.map((type) => (
                        <Option key={type.id} value={type.id}>
                          {type.name}
                        </Option>
                      ))}
                    </Select>
                  </InputGroup>

                  {transportTypeId === 2 && (
                    <InputGroup>
                      <Label>{t("tools.calculator.bookingType")}</Label>
                      <Row gutter={12}>
                        <Col span={12}>
                          <BookingTypeTile
                            selected={bookingType === "LCL"}
                            onClick={() => setBookingType("LCL")}
                          >
                            LCL
                          </BookingTypeTile>
                        </Col>
                        <Col span={12}>
                          <BookingTypeTile
                            selected={bookingType === "FCL"}
                            onClick={() => setBookingType("FCL")}
                          >
                            FCL
                          </BookingTypeTile>
                        </Col>
                      </Row>
                    </InputGroup>
                  )}

                  {transportTypeId &&
                    (transportTypeId !== 2 || bookingType) && (
                      <InputGroup>
                        <Label>{t("tools.calculator.route")}</Label>
                        <Select
                          placeholder={t("tools.calculator.selectRoute")}
                          value={selectedRoute}
                          onChange={setSelectedRoute}
                          style={{ width: "100%" }}
                          loading={routeOptions.length === 0}
                        >
                          {routeOptions.map((route) => (
                            <Option
                              key={route.key || route.value}
                              value={route.value}
                            >
                              {route.label}
                            </Option>
                          ))}
                        </Select>
                      </InputGroup>
                    )}

                  {transportTypeId &&
                    (transportTypeId !== 2 || bookingType) && (
                      <InputGroup>
                        <Label>{t("tools.calculator.incoterm")}</Label>
                        <Select
                          placeholder={t("tools.calculator.selectIncoterm")}
                          value={incotermId}
                          onChange={setIncotermId}
                          style={{ width: "100%" }}
                        >
                          {incoterms.map((incoterm) => (
                            <Option key={incoterm.id} value={incoterm.id}>
                              {incoterm.name}
                            </Option>
                          ))}
                        </Select>
                      </InputGroup>
                    )}
                </Space>
              </Card>
            </Col>
          </Row>
        </CalculatorContainer>
      </Space>
    </div>
  );
};
